import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Form, Button, Alert, Spinner } from "react-bootstrap";

/**
 * CardElementForm Component
 * Secure card input using Stripe Elements
 * Issue #16: PCI Compliance
 *
 * This component provides a PCI-compliant way to collect card information
 * using Stripe Elements. Card data never touches our servers, maintaining
 * PCI DSS compliance by leveraging Stripe's secure infrastructure.
 */
const CardElementForm = ({
  onSubmit,
  buttonText = "Submit",
  clientSecret = null,
  setupIntentMode = true,
  isLoading = false,
  error = null,
  showDefaultCheckbox = true,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState(null);
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Card element style options
  const cardElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#424770",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#dc3545",
        iconColor: "#dc3545",
      },
    },
    hidePostalCode: true,
  };

  // Handle card element change events
  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    setCardError(event.error ? event.error.message : null);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }

    if (!cardComplete) {
      // Card details not complete
      return;
    }

    // Prevent multiple submissions
    if (processing) return;

    setProcessing(true);
    setCardError(null);

    try {
      const cardElement = elements.getElement(CardElement);

      let result;

      if (setupIntentMode && clientSecret) {
        // For setup intents (adding payment methods)
        result = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {},
          },
        });
      } else if (clientSecret) {
        // For payment intents (making payments)
        result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {},
          },
        });
      } else {
        // Just create a payment method without a client secret
        const { paymentMethod, error } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        });

        if (error) {
          throw error;
        }

        // Return the payment method
        if (onSubmit) {
          onSubmit({ paymentMethod, setAsDefault });
        }
        setProcessing(false);
        return;
      }

      if (result.error) {
        // Handle error from Stripe
        setCardError(result.error.message);
      } else {
        // Payment or setup successful
        if (onSubmit) {
          if (setupIntentMode) {
            onSubmit({
              setupIntent: result.setupIntent,
              setAsDefault,
            });
          } else {
            onSubmit({
              paymentIntent: result.paymentIntent,
              setAsDefault,
            });
          }
        }
      }
    } catch (err) {
      setCardError(err.message || "An unexpected error occurred");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {/* Display errors from props */}
      {error && (
        <Alert variant="danger" className="mb-3">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Display card input errors */}
      {cardError && (
        <Alert variant="danger" className="mb-3">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {cardError}
        </Alert>
      )}

      <Form.Group className="mb-3">
        <Form.Label>Card Details</Form.Label>
        <div
          className="p-3 border rounded"
          style={{
            backgroundColor: "#f8f9fa",
            borderColor: cardError ? "#dc3545" : "#ced4da",
          }}
        >
          <CardElement
            id="card-element"
            options={cardElementOptions}
            onChange={handleCardChange}
          />
        </div>
        <Form.Text className="text-muted">
          <i className="fas fa-lock me-1"></i>
          Card details are securely processed by Stripe.
        </Form.Text>
      </Form.Group>

      {showDefaultCheckbox && (
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="set-default-checkbox"
            label="Set as default payment method"
            checked={setAsDefault}
            onChange={(e) => setSetAsDefault(e.target.checked)}
          />
        </Form.Group>
      )}

      <div className="d-flex align-items-center">
        <Button
          type="submit"
          variant="primary"
          disabled={!stripe || !cardComplete || processing || isLoading}
        >
          {processing || isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Processing...
            </>
          ) : (
            buttonText
          )}
        </Button>

        <div className="ms-3">
          <i
            className="fab fa-cc-visa mx-1 text-primary"
            style={{ fontSize: "1.5rem" }}
          ></i>
          <i
            className="fab fa-cc-mastercard mx-1 text-danger"
            style={{ fontSize: "1.5rem" }}
          ></i>
          <i
            className="fab fa-cc-amex mx-1 text-info"
            style={{ fontSize: "1.5rem" }}
          ></i>
        </div>
      </div>

      <div className="mt-3">
        <Alert variant="info" className="mb-0">
          <div className="d-flex align-items-center">
            <i
              className="fas fa-shield-alt me-3 text-primary"
              style={{ fontSize: "1.5rem" }}
            ></i>
            <small>
              Your payment information is processed securely through Stripe. We
              never store your card details on our servers.
            </small>
          </div>
        </Alert>
      </div>
    </Form>
  );
};

export default CardElementForm;
